import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@seller-hermes/utils/cn';

const TooltipProvider = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Provider>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>
>(({ delayDuration = 0, ...props }, ref) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
));
TooltipProvider.displayName = TooltipPrimitive.Provider.displayName;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * ⚠️ PADRÃO DO ui-v2 — camada FECHADA não pode engolir clique.
 *
 * O Radix desmonta o conteúdo no `animationend`. Quando esse evento não chega
 * (medido em Chrome: acontece), o nó fica montado, invisível, e continua
 * recebendo ponteiro — o sintoma é "o botão não funciona", às vezes sem nem
 * acender o hover, porque o gatilho fica DEBAIXO do fantasma do próprio
 * popover que ele abre.
 *
 * Dois tratamentos, e a escolha depende de quem bloqueia:
 *  - camada em POPPER (menu, popover, select, tooltip, hovercard, menu de
 *    contexto): além do guard, a animação de SAÍDA sai. O `[data-radix-popper-
 *    content-wrapper]` é PAI do conteúdo e não recebe classe nossa — só a
 *    desmontagem imediata tira ele do caminho;
 *  - camada em OVERLAY (dialog, alert, sheet, toast): o próprio elemento é o
 *    que cobre a tela, então o guard basta e a animação de saída fica (num
 *    modal ela é bem mais percebida que num menu).
 *
 * O `!` é obrigatório: o Radix escreve `pointer-events` INLINE ao fechar, e
 * estilo inline ganha de classe.
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin) text-balance z-50 rounded-md px-3 py-1.5 text-sm data-[state=closed]:!pointer-events-none',
        className,
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 h-2.5 w-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export default Object.assign(Tooltip, {
  Trigger: TooltipTrigger,
  Content: TooltipContent,
  Provider: TooltipProvider,
});
